import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { CHART_LABEL } from "./chartTokens";
import { CompareAxisTick } from "./CompareAxisTick";
import type { EvalResultSeries } from "./evalCompareModel";
import styles from "./EvaluationComparisonViewer.module.css";
import {
  MergedSeriesGradient,
  mergedSeriesPaint,
  useMergedSeriesGradientId,
} from "./MergedSeriesGradient";

// run 마다 값 하나를 막대로 그리는 공용 차트다. 성공률 비교와 소요 시간 비교가 쓴다.
export function CompareValueBarChart({
  title,
  items,
  value,
  format,
  domain,
  yAxisWidth,
  tickOffset,
}: {
  title: string;
  items: readonly EvalResultSeries[];
  value: (item: EvalResultSeries) => number;
  format: (value: number) => string;
  domain: (values: readonly number[]) => [number, number];
  yAxisWidth: number;
  tickOffset?: number;
}): ReactNode {
  const mergedGradientId = useMergedSeriesGradientId();
  // 카테고리 축의 값은 run 키다. 정책 이름은 겹칠 수 있다. 이름을 축 값으로 쓰면 같은 이름의
  // run 이 한 막대로 합쳐져 경고 없이 사라진다. 그래서 눈금에 표시하는 글자만 이름으로 바꾼다.
  const data = items.map((item) => ({
    id: item.key,
    tag: item.key,
    value: value(item),
    color: item.color,
  }));
  const colorByTag = Object.fromEntries(items.map((item) => [item.key, item.color]));
  const labelByTag = Object.fromEntries(items.map((item) => [item.key, item.label]));
  return (
    <div className={styles.chartCard}>
      <span className={styles.chartTitle}>{title}</span>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={data} margin={{ top: 24, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <MergedSeriesGradient id={mergedGradientId} />
          </defs>
          <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
          <XAxis
            dataKey="tag"
            interval={0}
            height={46}
            tickLine={false}
            axisLine={false}
            tick={
              <CompareAxisTick
                colorByValue={colorByTag}
                labelByValue={labelByTag}
                offset={tickOffset}
              />
            }
          />
          <YAxis
            width={yAxisWidth}
            domain={domain(data.map((point) => point.value))}
            tickFormatter={(tick) => format(Number(tick))}
            stroke={CHART_CHROME.axis}
            fontSize={11}
          />
          <Tooltip
            cursor={{ fill: "transparent" }}
            // 툴팁 제목도 기본값이 축 값(run 키)이다. 그래서 이름으로 바꾼다.
            labelFormatter={(tooltipLabel) => labelByTag[String(tooltipLabel)] ?? tooltipLabel}
            formatter={(tooltipValue) => format(Number(tooltipValue))}
            contentStyle={{
              background: CHART_CHROME.tooltipBg,
              borderColor: CHART_CHROME.tooltipBorder,
              color: CHART_CHROME.tooltipInk,
            }}
            itemStyle={{ color: CHART_CHROME.tooltipInk }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((point) => (
              <Cell
                key={point.id}
                fill={mergedSeriesPaint(point.id, point.color, mergedGradientId)}
              />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              // recharts 의 LabelFormatter 는 boolean·null·undefined 도 받는다. 타입을 좁게
              // 적으면 대입할 수 없다. 생략하면 문맥 추론이 적용되지 않아 implicit any 가 된다.
              formatter={(label: string | number | boolean | null | undefined) =>
                format(Number(label))
              }
              fill={CHART_LABEL}
              fontSize={12}
              fontWeight={600}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
